import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, ArrowRight, Check, X, AlertTriangle, Sparkles,
  Database, Globe, Webhook, Layers, Zap, ShieldCheck, Truck, CreditCard,
  ShoppingCart, Banknote, Bot, Building2, Plug, FileText, ScrollText,
  GitBranch, Eye, EyeOff, Cog, ChevronDown, ChevronRight, Search,
} from "lucide-react";

// ──────────────────────────────────────────────────────────────────────────
// verqo Architecture Map — interactive blueprint of every portal, route,
// API, integration, and flow in the platform. Source-of-truth derived from
// /app/backend/server.py (api routes), /app/frontend/src/App.js (routes),
// /app/backend/services/feature_flags.py, /app/backend/providers/*.
// Update this file when architecture changes.
// ──────────────────────────────────────────────────────────────────────────

const STATUS = {
  live:    { label: "LIVE",     color: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30" },
  mocked:  { label: "MOCKED",   color: "bg-amber-500/15 text-amber-700 border-amber-500/30" },
  hidden:  { label: "HIDDEN",   color: "bg-zinc-500/15 text-zinc-600 border-zinc-500/30" },
  blocked: { label: "BLOCKED",  color: "bg-rose-500/15 text-rose-700 border-rose-500/30" },
  pending: { label: "PENDING",  color: "bg-sky-500/15 text-sky-700 border-sky-500/30" },
};

const StatusBadge = ({ s }) => {
  const cfg = STATUS[s] || STATUS.live;
  return (
    <span className={`text-[9.5px] mono uppercase tracking-[0.18em] px-1.5 py-0.5 rounded border ${cfg.color}`}>
      {cfg.label}
    </span>
  );
};

// ── PORTALS ───────────────────────────────────────────────────────────────
const PORTALS = [
  {
    id: "public", name: "Public / Marketing", icon: Globe, accent: "from-foreground/90 to-foreground",
    desc: "Pre-auth surfaces. Where visitors decide to sign up.",
    routes: [
      { path: "/",                page: "Landing.jsx",        s: "live", note: "Stripe-tier hero, persona-aware sections" },
      { path: "/preview/dark",    page: "LandingDark.jsx",    s: "live", note: "Alt dark variant" },
      { path: "/login",           page: "Login.jsx",          s: "live", note: "Persona toggle (buyer/seller)" },
      { path: "/register",        page: "Register.jsx",       s: "live", note: "Persona toggle, ?as= param" },
      { path: "/demo",            page: "DemoLanding.jsx",    s: "live", note: "Pitch portal, prefilled demo data" },
    ],
  },
  {
    id: "buyer", name: "Buyer Portal", icon: ShoppingCart, accent: "from-emerald-500/80 to-emerald-700",
    desc: "Wholesale buyers. Order, finance, ship.",
    routes: [
      { path: "/buyer",                page: "BuyerDashboard",    s: "live" },
      { path: "/register",          page: "Register",          s: "live", note: "Unified 4-step wizard (KYB + identity verification + categories)" },
      { path: "/buyer/catalog",        page: "BuyerCatalog",      s: "live" },
      { path: "/buyer/cart",           page: "BuyerCart",         s: "live" },
      { path: "/buyer/orders",         page: "BuyerOrders",       s: "live", note: "Journey/All/Awaiting/In-transit/Delivered" },
      { path: "/buyer/payments",       page: "BuyerPayments",     s: "live", note: "Wallet/Credit/Methods/Activity" },
      { path: "/buyer/po-generator",   page: "BuyerPOGenerator",  s: "hidden", note: "Behind po_generator flag" },
      { path: "/buyer/score",          page: "BuyerScore",        s: "hidden", note: "Behind buyer_score flag" },
      { path: "/buyer/capital",        page: "BuyerCapital",      s: "live", note: "Triggers AI underwriter on apply" },
      { path: "/buyer/shipping",       page: "BuyerShipping",     s: "live" },
      { path: "/buyer/suppliers",      page: "BuyerSuppliers",    s: "hidden" },
      { path: "/buyer/inventory",      page: "BuyerInventory",    s: "hidden" },
      { path: "/buyer/rfq",            page: "BuyerRfq",          s: "hidden" },
      { path: "/buyer/dropship",       page: "BuyerDropship",     s: "hidden" },
      { path: "/buyer/reports",        page: "BuyerReports",      s: "hidden" },
      { path: "/buyer/ai",             page: "BuyerAI",           s: "hidden", note: "AI Assistant placeholder" },
      { path: "/buyer/apps",           page: "BuyerAppStore",     s: "hidden" },
      { path: "/buyer/settings",       page: "BuyerSettings",     s: "live" },
    ],
  },
  {
    id: "seller", name: "Seller Portal", icon: Building2, accent: "from-accent to-accent/70",
    desc: "Wholesale brands & suppliers. Sell, get paid D+1.",
    routes: [
      { path: "/seller",            page: "SellerDashboard", s: "live" },
      { path: "/seller/orders",     page: "SellerOrders",    s: "live" },
      { path: "/seller/catalog",    page: "SellerCatalog",   s: "live" },
      { path: "/seller/invoices",   page: "SellerInvoices",  s: "live" },
      { path: "/seller/wallet",     page: "SellerWallet",    s: "live", note: "D+1 payouts (Modern Treasury)" },
      { path: "/seller/buyers",     page: "SellerBuyers",    s: "live", note: "Per-buyer credit offers" },
      { path: "/seller/insurance",  page: "SellerInsurance", s: "live" },
      { path: "/seller/dropship",   page: "SellerDropship",  s: "hidden" },
      { path: "/seller/apps",       page: "SellerAppStore",  s: "hidden" },
    ],
  },
  {
    id: "admin", name: "Admin Portal", icon: ShieldCheck, accent: "from-sky-500/80 to-sky-700",
    desc: "Platform operator. Risk, finance, integrations.",
    routes: [
      { path: "/admin",               page: "AdminDashboard",     s: "live" },
      { path: "/admin/users",         page: "AdminUsers",         s: "live" },
      { path: "/admin/buyers",        page: "AdminBuyers",        s: "live" },
      { path: "/admin/commerce",      page: "AdminCommerce",      s: "live" },
      { path: "/admin/credit",        page: "AdminCredit",        s: "live", note: "AI memo + auto-grant rules" },
      { path: "/admin/finance",       page: "AdminFinance",       s: "live" },
      { path: "/admin/insurance",     page: "AdminInsurance",     s: "live" },
      { path: "/admin/activity",      page: "AdminActivity",      s: "live" },
      { path: "/admin/integrations",  page: "AdminIntegrations",  s: "live", note: "Live status of all 3rd-party providers" },
    ],
  },
];

// ── PROVIDERS / 3rd party integrations ────────────────────────────────────
const PROVIDERS = [
  { id: "plaid",     name: "Plaid",            cat: "Bank linking",   s: "live",    file: "providers/plaid_provider.py" },
  { id: "middesk",   name: "Middesk",          cat: "KYB / UCC",      s: "blocked", note: "Live key returns 401 — pending Middesk support", file: "providers/middesk.py" },
  { id: "claude",    name: "Verqo AI",         cat: "AI Underwriter", s: "live",    note: "Emergent LLM key", file: "providers/ai_underwriter.py" },
  { id: "mt",        name: "Modern Treasury",  cat: "Ledger / Wallet",s: "live",    file: "providers/modern_treasury.py" },
  { id: "verqo-shipping",    name: "Verqo Shipping",           cat: "Parcel",         s: "pending", note: "Live key set in .env — verify Emergent deploy env", file: "providers/shipping.py" },
  { id: "warp",      name: "Warp",             cat: "LTL / FTL",      s: "live",    file: "providers/shipping.py" },
  { id: "catalog",   name: "Catalog (GCP)",    cat: "Product sync",   s: "blocked", note: "Upstream Cloud Run instance returns 503", file: "providers/catalog.py" },
  { id: "twoinc",    name: "Two.inc",          cat: "BNPL / Net-terms", s: "mocked",note: "Provider abstraction ready, awaiting API keys" },
  { id: "twilio",    name: "Twilio",           cat: "SMS notify",     s: "pending", note: "Planned (P1) — not wired yet" },
  { id: "resend",    name: "Resend",           cat: "Email notify",   s: "pending", note: "Planned (P1) — not wired yet" },
  { id: "sentry",    name: "Sentry",           cat: "Errors",         s: "pending", note: "DSN required from user" },
];

// ── KEY FLOWS — multi-step user journeys ─────────────────────────────────
const FLOWS = [
  {
    id: "onboard", name: "Buyer Onboarding & AI Underwriting", icon: Bot, color: "text-emerald-700",
    summary: "From sign-up to autonomous credit grant — fully automated.",
    steps: [
      { actor: "Buyer",   action: "Signs up at /register?as=buyer",            api: "POST /api/auth/register" },
      { actor: "Buyer",   action: "KYB form (legal name, EIN, address)",       api: "POST /api/onboarding/kyb/submit", trig: "→ Middesk KYB lookup" },
      { actor: "Buyer",   action: "Plaid Link → connect bank",                 api: "POST /api/onboarding/plaid/link-token + /exchange" },
      { actor: "System",  action: "Background task fetches transactions",      api: "Plaid /transactions/get", trig: "FastAPI BackgroundTask" },
      { actor: "AI",      action: "Verqo AI generates credit memo",            api: "providers/ai_underwriter.py", trig: "After KYB+Plaid both succeed" },
      { actor: "Engine",  action: "Auto-grant rules → set Net-30 limit",       api: "POST /api/admin/integrations/ai/auto-grant" },
      { actor: "Buyer",   action: "Lands in /buyer with credit unlocked",      api: "GET /api/auth/me + /capital/summary" },
    ],
  },
  {
    id: "order", name: "Order → Pay → Pack → Ship", icon: Truck, color: "text-sky-700",
    summary: "End-to-end commerce loop across both portals.",
    steps: [
      { actor: "Buyer",   action: "Adds to cart (localStorage)",              api: "/buyer/cart" },
      { actor: "Buyer",   action: "Confirm & place order",                    api: "POST /api/orders" },
      { actor: "Seller",  action: "Confirms order (auto or manual)",          api: "POST /api/orders/{oid}/confirm" },
      { actor: "Buyer",   action: "Pay invoice (wallet/credit/ACH/wire/card)",api: "POST /api/invoices/{iid}/pay" },
      { actor: "System",  action: "Order auto-advances to processing",        api: "internal status machine" },
      { actor: "Seller",  action: "Books shipping (Verqo Shipping)",             api: "POST /api/shipping/book" },
      { actor: "System",  action: "Tracking webhooks update order",           api: "POST /api/webhooks/warp", trig: "Webhook" },
    ],
  },
  {
    id: "finance", name: "Invoice Factoring & Capital", icon: Banknote, color: "text-amber-700",
    summary: "Sellers get paid D+1; buyers tap Net-30/60/90.",
    steps: [
      { actor: "Seller",  action: "Requests factoring on invoice (97% payout)", api: "POST /api/invoices/{iid}/factor" },
      { actor: "System",  action: "Funds released via Modern Treasury",         api: "providers/modern_treasury.py" },
      { actor: "Buyer",   action: "Applies for capital",                        api: "POST /api/credit/apply" },
      { actor: "AI",      action: "Generates underwriting memo",                api: "POST /api/admin/credit/memo/{buyer_id}", trig: "On-demand or background" },
      { actor: "Admin",   action: "Reviews & approves / revokes",               api: "PATCH /api/credit/applications/{aid}" },
    ],
  },
  {
    id: "insurance", name: "Trade Credit Insurance", icon: ShieldCheck, color: "text-rose-700",
    summary: "Per-invoice coverage. Currently mocked underwriting & payouts.",
    steps: [
      { actor: "Seller",  action: "Requests insurance on invoice",       api: "POST /api/insurance" },
      { actor: "System",  action: "Auto-quote based on score",           api: "POST /api/insurance/applications/{aid}/auto-approve", trig: "Mocked policy issuance" },
      { actor: "Seller",  action: "Files claim if buyer defaults",       api: "POST /api/insurance/claims" },
      { actor: "Admin",   action: "Reviews evidence & advances payout",  api: "PATCH /api/insurance/claims/{cid}/advance", trig: "Mocked payout" },
    ],
  },
];

// ── BACKGROUND TRIGGERS / WEBHOOKS ───────────────────────────────────────
const TRIGGERS = [
  { type: "Webhook",    endpoint: "POST /api/webhooks/plaid",   purpose: "Plaid item updates / transactions",       s: "live" },
  { type: "Webhook",    endpoint: "POST /api/webhooks/middesk", purpose: "KYB completion callbacks",                s: "live" },
  { type: "Webhook",    endpoint: "POST /api/webhooks/warp",    purpose: "Freight tracking updates",                s: "live" },
  { type: "Webhook",    endpoint: "POST /api/webhooks/shippo",  purpose: "Parcel tracking — NOT YET BUILT (P3)",    s: "pending" },
  { type: "Background", endpoint: "ai_underwriter.run_for_buyer", purpose: "Auto-fires post-KYB & post-Plaid",       s: "live" },
  { type: "Background", endpoint: "auto_grant_rules.evaluate",   purpose: "Net-30 grant after AI memo",              s: "live" },
  { type: "Cron-like",  endpoint: "demo_seeder",                purpose: "Reseeds demo data via /api/demo/start",   s: "live" },
];

// ── BACKEND API SURFACE (grouped) ────────────────────────────────────────
const API_GROUPS = [
  { name: "Auth",          icon: ShieldCheck, endpoints: ["POST /auth/login", "POST /auth/register", "POST /auth/logout", "GET /auth/me"] },
  { name: "Onboarding",    icon: Sparkles,    endpoints: ["POST /onboarding/kyb/submit", "GET /onboarding/kyb/status", "POST /onboarding/plaid/link-token", "POST /onboarding/plaid/exchange", "POST /onboarding/plaid/refresh", "GET /onboarding/plaid/summary", "GET /kyb", "POST /kyb"] },
  { name: "Orders",        icon: ShoppingCart,endpoints: ["GET /orders", "POST /orders", "GET /orders/{oid}", "PATCH /orders/{oid}/status", "POST /orders/{oid}/confirm", "POST /orders/{oid}/reorder", "POST /orders/po-upload", "POST /orders/po-confirm"] },
  { name: "Invoices",      icon: FileText,    endpoints: ["GET /invoices", "GET /invoices/summary", "POST /invoices/{iid}/pay", "POST /invoices/{iid}/sell", "PATCH /invoices/{iid}/authorize"] },
  { name: "Payments",      icon: CreditCard,  endpoints: ["GET /payment-methods", "POST /payment-methods", "PATCH /payment-methods/{pmid}/default", "DELETE /payment-methods/{pmid}", "POST /payments/multi-pay"] },
  { name: "Wallet",        icon: Banknote,    endpoints: ["GET /wallet", "POST /wallet/topup", "POST /wallet/withdraw", "GET /wallet/payment/{payment_order_id}"] },
  { name: "Credit",        icon: Bot,         endpoints: ["GET /credit/applications", "POST /credit/apply", "PATCH /credit/applications/{aid}", "GET /capital/summary", "POST /admin/credit/memo/{buyer_id}", "GET /admin/credit/memo/{buyer_id}", "POST /admin/credit/grant/{buyer_id}/revoke", "POST /admin/integrations/ai/auto-grant"] },
  { name: "Score",         icon: Sparkles,    endpoints: ["GET /score", "GET /score/{buyer_id}", "GET /scoring/events", "GET /scoring/history", "GET /scoring/peers"] },
  { name: "Catalog",       icon: Layers,      endpoints: ["GET /products", "GET /products/all", "POST /products", "DELETE /products/{pid}", "GET /catalog/external", "POST /admin/catalog/sync", "GET /admin/catalog/sync-status"] },
  { name: "Shipping",      icon: Truck,       endpoints: ["GET /shipping", "POST /shipping/rates", "POST /shipping/book", "GET /shipping/track/{carrier}/{tracking_number}"] },
  { name: "Insurance",     icon: ShieldCheck, endpoints: ["GET /insurance", "POST /insurance", "POST /insurance/request", "GET /insurance/applications", "POST /insurance/applications", "POST /insurance/applications/{aid}/auto-approve", "GET /insurance/claims", "POST /insurance/claims", "POST /insurance/claims/{cid}/auto-approve", "POST /insurance/claims/{cid}/evidence", "PATCH /insurance/claims/{cid}/advance"] },
  { name: "Seller offers", icon: Building2,   endpoints: ["GET /seller/credit-offers", "POST /seller/credit-offers", "PATCH /seller/credit-offers/{oid}", "DELETE /seller/credit-offers/{oid}", "GET /buyer/seller-terms"] },
  { name: "Admin",         icon: ShieldCheck, endpoints: ["GET /admin/overview", "GET /admin/users", "GET /admin/buyers", "GET /admin/sellers", "GET /admin/orders", "GET /admin/invoices", "GET /admin/seller-offers", "GET /admin/audit-log", "GET /admin/system-health", "GET /admin/dropship-orders"] },
  { name: "Integrations",  icon: Plug,        endpoints: ["GET /admin/integrations/environment", "GET /admin/integrations/ai/status", "GET /admin/integrations/middesk/status", "GET /admin/integrations/plaid/status", "GET /admin/integrations/mt/status", "GET /admin/integrations/mt/internal-accounts", "GET /admin/integrations/shipping/status"] },
  { name: "Misc",          icon: Cog,         endpoints: ["GET /health", "GET /config", "POST /demo/start", "GET /activity", "GET /addresses", "POST /addresses", "DELETE /addresses/{aid}", "GET /dashboard/buyer", "PATCH /profile", "GET /reports/summary", "GET /spend/trend", "GET /statements"] },
];

// ── DATA MODELS ──────────────────────────────────────────────────────────
const MODELS = [
  { name: "User",          file: "models/user.py",      keys: ["role", "company", "ai_credit_grant", "kyb_status"] },
  { name: "Order",         file: "models/order.py",     keys: ["status", "items[]", "buyer_id", "seller_id"] },
  { name: "Invoice",       file: "models/invoice.py",   keys: ["status", "terms", "factoring_status", "due_at"] },
  { name: "Product",       file: "models/product.py",   keys: ["seller_id", "moq", "tiers[]", "stock"] },
  { name: "Credit",        file: "models/credit.py",    keys: ["limit", "utilized", "ai_memo_id", "decision"] },
  { name: "Finance",       file: "models/finance.py",   keys: ["wallet_balance", "ledger_entries[]"] },
  { name: "Insurance",     file: "models/insurance.py", keys: ["policy_id", "premium_bps", "claims[]"] },
  { name: "Shipping",      file: "models/shipping.py",  keys: ["carrier", "tracking", "mode (parcel/freight)"] },
  { name: "Misc",          file: "models/misc.py",      keys: ["addresses", "audit_log", "scoring_events"] },
];

// ── COMPONENT ────────────────────────────────────────────────────────────
export default function Architecture() {
  const [tab, setTab] = useState("portals");
  const [openPortal, setOpenPortal] = useState("buyer");
  const [openFlow, setOpenFlow] = useState("onboard");
  const [filter, setFilter] = useState("");

  const tabs = [
    { id: "portals",      label: "Portals & Routes", icon: Globe },
    { id: "flows",        label: "User Flows",       icon: GitBranch },
    { id: "providers",    label: "Integrations",     icon: Plug },
    { id: "api",          label: "API Surface",      icon: Webhook },
    { id: "triggers",     label: "Triggers",         icon: Zap },
    { id: "models",       label: "Data Models",      icon: Database },
    { id: "flags",        label: "Feature Flags",    icon: Eye },
  ];

  // Stats for header
  const stats = useMemo(() => {
    const allRoutes = PORTALS.flatMap(p => p.routes);
    const live = allRoutes.filter(r => r.s === "live").length;
    const hidden = allRoutes.filter(r => r.s === "hidden").length;
    const provLive = PROVIDERS.filter(p => p.s === "live").length;
    const provBlocked = PROVIDERS.filter(p => p.s === "blocked").length;
    return {
      pages: allRoutes.length, live, hidden,
      apis: API_GROUPS.reduce((n, g) => n + g.endpoints.length, 0),
      provLive, provBlocked,
      flows: FLOWS.length,
    };
  }, []);

  const filterProviders = useMemo(() =>
    PROVIDERS.filter(p =>
      !filter || p.name.toLowerCase().includes(filter.toLowerCase()) || p.cat.toLowerCase().includes(filter.toLowerCase())
    ), [filter]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Top bar */}
      <div className="sticky top-0 z-40 backdrop-blur-md bg-background/85 border-b border-foreground/10">
        <div className="max-w-7xl mx-auto px-6 py-3.5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="arch-back">
            <div className="w-7 h-7 rounded-md bg-foreground grid place-items-center">
              <span className="text-accent font-bold text-[13px] display">v</span>
            </div>
            <span className="display font-semibold text-[16px] tracking-tight">verqo</span>
            <span className="text-foreground/40 mx-1.5">/</span>
            <span className="display text-[15px] tracking-tight">Architecture Map</span>
          </Link>
          <div className="flex items-center gap-2 text-[12px] text-foreground/60">
            <span className="hidden md:inline mono uppercase tracking-[0.18em] text-[10.5px]">Internal blueprint</span>
            <Link to="/admin" className="text-foreground/80 hover:text-foreground inline-flex items-center gap-1" data-testid="arch-admin-link">
              Open Admin <ArrowUpRight className="w-3.5 h-3.5"/>
            </Link>
          </div>
        </div>
      </div>

      {/* Hero / stats */}
      <section className="max-w-7xl mx-auto px-6 pt-12 pb-8">
        <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-foreground/55 inline-flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-accent shadow-[0_0_10px_var(--accent,#e8ff3a)]"/>
          Live — auto-derived from current codebase
        </div>
        <h1 className="display text-4xl sm:text-5xl mt-3 leading-[1.05] tracking-tight max-w-3xl">
          Every portal, route, flow, and integration on verqo — at a glance.
        </h1>
        <p className="text-foreground/60 mt-4 max-w-2xl text-[15px] leading-relaxed">
          A blueprint of the platform. Use this to spot what's live, what's mocked, what's hidden behind feature flags, and what still needs wiring before launch.
        </p>

        <div className="mt-8 grid grid-cols-2 md:grid-cols-6 gap-3">
          {[
            { k: "Pages",         v: stats.pages },
            { k: "Live pages",    v: stats.live },
            { k: "Hidden (flag)", v: stats.hidden },
            { k: "API endpoints", v: stats.apis },
            { k: "Providers live",v: stats.provLive },
            { k: "Blocked",       v: stats.provBlocked },
          ].map(s => (
            <div key={s.k} className="rounded-xl border border-foreground/10 bg-secondary/30 p-4">
              <div className="display text-3xl tracking-tight">{s.v}</div>
              <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55 mt-1">{s.k}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Tabs */}
      <section className="max-w-7xl mx-auto px-6">
        <div className="flex flex-wrap gap-1.5 border-b border-foreground/10 pb-2">
          {tabs.map(t => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`arch-tab-${t.id}`}
                className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[13px] transition-all ${active ? "bg-foreground text-background" : "text-foreground/60 hover:text-foreground hover:bg-secondary/50"}`}
              >
                <Icon className="w-3.5 h-3.5"/>{t.label}
              </button>
            );
          })}
        </div>
      </section>

      {/* Body */}
      <section className="max-w-7xl mx-auto px-6 py-10">
        {tab === "portals" && (
          <div className="grid lg:grid-cols-2 gap-5" data-testid="arch-portals">
            {PORTALS.map(p => {
              const Icon = p.icon;
              const isOpen = openPortal === p.id;
              const live = p.routes.filter(r => r.s === "live").length;
              return (
                <div key={p.id} className="rounded-2xl border border-foreground/10 bg-white overflow-hidden">
                  <button
                    onClick={() => setOpenPortal(isOpen ? null : p.id)}
                    className="w-full text-left p-5 flex items-center gap-4 hover:bg-secondary/30 transition-colors"
                    data-testid={`arch-portal-${p.id}`}
                  >
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${p.accent} grid place-items-center text-background shrink-0`}>
                      <Icon className="w-5 h-5"/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <h3 className="display text-xl tracking-tight">{p.name}</h3>
                        <span className="text-[11px] mono text-foreground/50">{live}/{p.routes.length} live</span>
                      </div>
                      <p className="text-[13px] text-foreground/60 mt-0.5">{p.desc}</p>
                    </div>
                    {isOpen ? <ChevronDown className="w-4 h-4 text-foreground/40"/> : <ChevronRight className="w-4 h-4 text-foreground/40"/>}
                  </button>
                  {isOpen && (
                    <div className="border-t border-foreground/10 divide-y divide-foreground/5">
                      {p.routes.map(r => (
                        <div key={r.path} className="p-3.5 px-5 flex items-start gap-3 hover:bg-secondary/20">
                          <code className="text-[12px] mono text-foreground/80 shrink-0">{r.path}</code>
                          <div className="flex-1 min-w-0">
                            <div className="text-[12px] text-foreground/50">{r.page}</div>
                            {r.note && <div className="text-[12px] text-foreground/70 mt-0.5">{r.note}</div>}
                          </div>
                          <StatusBadge s={r.s}/>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {tab === "flows" && (
          <div className="space-y-5" data-testid="arch-flows">
            {FLOWS.map(f => {
              const Icon = f.icon;
              const isOpen = openFlow === f.id;
              return (
                <div key={f.id} className="rounded-2xl border border-foreground/10 bg-white overflow-hidden">
                  <button
                    onClick={() => setOpenFlow(isOpen ? null : f.id)}
                    className="w-full text-left p-5 flex items-center gap-4 hover:bg-secondary/30"
                    data-testid={`arch-flow-${f.id}`}
                  >
                    <div className="w-11 h-11 rounded-xl bg-secondary grid place-items-center shrink-0">
                      <Icon className={`w-5 h-5 ${f.color}`}/>
                    </div>
                    <div className="flex-1">
                      <h3 className="display text-xl tracking-tight">{f.name}</h3>
                      <p className="text-[13px] text-foreground/60 mt-0.5">{f.summary}</p>
                    </div>
                    <span className="text-[11px] mono text-foreground/50">{f.steps.length} steps</span>
                    {isOpen ? <ChevronDown className="w-4 h-4 text-foreground/40"/> : <ChevronRight className="w-4 h-4 text-foreground/40"/>}
                  </button>
                  {isOpen && (
                    <ol className="border-t border-foreground/10">
                      {f.steps.map((s, i) => (
                        <li key={i} className="flex items-start gap-4 p-4 px-5 border-b last:border-0 border-foreground/5">
                          <div className="w-7 h-7 rounded-full bg-foreground text-background grid place-items-center text-[12px] mono shrink-0">{i + 1}</div>
                          <div className="flex-1">
                            <div className="flex items-baseline gap-2 flex-wrap">
                              <span className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">{s.actor}</span>
                              <span className="text-[14px] text-foreground/85">{s.action}</span>
                            </div>
                            {s.api && <code className="block mt-1 text-[11.5px] mono text-foreground/55">{s.api}</code>}
                            {s.trig && <div className="mt-1 text-[12px] text-foreground/60 inline-flex items-center gap-1.5"><Zap className="w-3 h-3 text-amber-600"/>{s.trig}</div>}
                          </div>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {tab === "providers" && (
          <div data-testid="arch-providers">
            <div className="flex items-center gap-2 mb-5">
              <div className="relative flex-1 max-w-sm">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40"/>
                <input
                  value={filter}
                  onChange={e => setFilter(e.target.value)}
                  placeholder="Filter integrations…"
                  className="w-full pl-9 pr-3 py-2 rounded-full border border-foreground/15 bg-white text-[13px] focus:outline-none focus:border-foreground/40"
                  data-testid="arch-prov-filter"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filterProviders.map(p => (
                <div key={p.id} className="rounded-xl border border-foreground/10 bg-white p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="display text-[16px] tracking-tight">{p.name}</div>
                      <div className="text-[11px] mono uppercase tracking-[0.18em] text-foreground/50 mt-0.5">{p.cat}</div>
                    </div>
                    <StatusBadge s={p.s}/>
                  </div>
                  {p.note && <p className="text-[12.5px] text-foreground/65 mt-3">{p.note}</p>}
                  {p.file && <code className="block mt-3 text-[11px] mono text-foreground/45">{p.file}</code>}
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "api" && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="arch-api">
            {API_GROUPS.map(g => {
              const Icon = g.icon;
              return (
                <div key={g.name} className="rounded-xl border border-foreground/10 bg-white overflow-hidden">
                  <div className="p-4 flex items-center gap-2.5 border-b border-foreground/5">
                    <Icon className="w-4 h-4 text-foreground/70"/>
                    <h4 className="display text-[15px] tracking-tight">{g.name}</h4>
                    <span className="ml-auto text-[10.5px] mono text-foreground/45">{g.endpoints.length}</span>
                  </div>
                  <ul className="p-3 max-h-64 overflow-y-auto space-y-1">
                    {g.endpoints.map(e => (
                      <li key={e}>
                        <code className="block text-[11.5px] mono text-foreground/75 px-2 py-1 rounded hover:bg-secondary/60">
                          {e}
                        </code>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}

        {tab === "triggers" && (
          <div className="rounded-2xl border border-foreground/10 bg-white overflow-hidden" data-testid="arch-triggers">
            <table className="w-full text-[13.5px]">
              <thead className="bg-secondary/40 text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">
                <tr><th className="text-left p-4">Type</th><th className="text-left p-4">Endpoint / Hook</th><th className="text-left p-4">Purpose</th><th className="text-left p-4">Status</th></tr>
              </thead>
              <tbody className="divide-y divide-foreground/5">
                {TRIGGERS.map((t, i) => (
                  <tr key={i} className="hover:bg-secondary/20">
                    <td className="p-4">
                      <span className="text-[11px] mono uppercase tracking-[0.18em] text-foreground/60">{t.type}</span>
                    </td>
                    <td className="p-4"><code className="text-[12px] mono">{t.endpoint}</code></td>
                    <td className="p-4 text-foreground/75">{t.purpose}</td>
                    <td className="p-4"><StatusBadge s={t.s}/></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "models" && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="arch-models">
            {MODELS.map(m => (
              <div key={m.name} className="rounded-xl border border-foreground/10 bg-white p-4">
                <div className="display text-[16px] tracking-tight">{m.name}</div>
                <code className="block text-[11px] mono text-foreground/45 mt-1">{m.file}</code>
                <ul className="mt-3 space-y-1">
                  {m.keys.map(k => (
                    <li key={k} className="text-[12.5px] text-foreground/75 flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-foreground/40"/>
                      <code className="mono">{k}</code>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        {tab === "flags" && (
          <FlagsPanel/>
        )}
      </section>

      <footer className="border-t border-foreground/10 mt-12">
        <div className="max-w-7xl mx-auto px-6 py-8 text-[12px] text-foreground/55 flex flex-wrap items-center justify-between gap-3">
          <div>Source-of-truth: <code className="mono">/app/backend/server.py</code> · <code className="mono">/app/frontend/src/App.js</code> · <code className="mono">/app/backend/services/feature_flags.py</code></div>
          <div className="mono uppercase tracking-[0.18em] text-[10.5px]">Internal · do not share publicly</div>
        </div>
      </footer>
    </div>
  );
}

// ── Feature flags panel — fetches /api/config ─────────────────────────────
function FlagsPanel() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/config`)
      .then(r => r.json())
      .then(d => { if (!cancelled) { setCfg(d); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div className="text-[13px] text-foreground/55">Loading feature flags…</div>;
  if (!cfg) return <div className="text-[13px] text-rose-700">Failed to load /api/config</div>;

  const features = cfg.features || {};
  const items = Object.entries(features).map(([k, v]) => ({ id: k, visible: v }));

  return (
    <div data-testid="arch-flags">
      <div className="rounded-xl border border-foreground/10 bg-white p-4 mb-5 flex flex-wrap items-center gap-3">
        <span className="text-[11px] mono uppercase tracking-[0.18em] text-foreground/55">Active mode</span>
        <span className="display text-[16px] tracking-tight">{cfg.app_mode}</span>
        {cfg.banner?.show && (
          <span className="text-[11.5px] text-foreground/65">— {cfg.banner.message}</span>
        )}
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map(it => (
          <div key={it.id} className="rounded-lg border border-foreground/10 bg-white p-3 flex items-center gap-3">
            <span className={`w-7 h-7 rounded grid place-items-center ${it.visible ? "bg-emerald-500/15 text-emerald-700" : "bg-zinc-500/15 text-zinc-500"}`}>
              {it.visible ? <Eye className="w-3.5 h-3.5"/> : <EyeOff className="w-3.5 h-3.5"/>}
            </span>
            <div className="flex-1 min-w-0">
              <code className="block text-[12px] mono text-foreground/85 truncate">{it.id}</code>
              <div className="text-[11px] text-foreground/50">{it.visible ? "Visible to users" : "Hidden by flag"}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
